// The reconciliation authority: validate proposed intents against canonical
// state and turn the survivors into accepted, revved ops.
//
// Pure. Takes a room and intents, returns ops + rejections; the caller persists
// and broadcasts. Living in shared/ (rather than inside the DO) means jam-sim
// and the worker test suite exercise the REAL merge, not a mirror of it — the
// duplication that jam-sim's README calls out as its own bug class.
//
// Conflict policy: REJECT stale, never transform. A rejection round-trips the
// room's current value to the proposer, which then re-derives; a transform would
// silently reinterpret intent and is exactly the class of cleverness that made
// v2's rewrite/correction path so hard to reason about. Predictable beats smooth
// here — see the open question in docs/project/do-authoritative-sync.md.

import { isInstrumentId } from "../instruments.js";
import { MAX_NOTES_PER_CLIP } from "../midi/notes.js";
import { CENTER_PAN, UNITY_VOLUME, clampPan, clampVolume } from "../mixer.js";
import { ASSERT_EPS } from "./canonical.js";
import { chainMatches } from "./devices.js";
import { parseTrackName, renameKeepingToken } from "./track-id.js";
import type { Intent, Rejection } from "./intents.js";
import { MAX_DETUNE, MAX_LIBRARY_REF_ID, MAX_RENDER_FIELD, MAX_TRANSPOSE, MAX_WARP_MARKERS, type ClipData, type WarpMarkerData, warpSettingsOf, wireFileName } from "./protocol.js";
import {
  applyAcceptedOps,
  clipsOfTrack,
  cloneRoom,
  type AcceptedOp,
  type CanonicalRoom,
  MAX_SCENES,
  MAX_SCENE_NAME_LENGTH,
  MAX_CUE_POINTS,
  MAX_CUE_NAME_LENGTH,
  MAX_RETURNS,
  isChainOwner,
  type DeviceState,
  MAX_DEVICES,
  MAX_DEVICE_PARAMS,
  MAX_DEVICE_PARAMS_PER_LANE,
  MAX_DEVICE_PARAM_ITEMS,
  MAX_DEVICE_NAME_LENGTH,
  cloneDevices,
  clampParam, validScale } from "./state.js";

export interface MergeResult {
  accepted: AcceptedOp[];
  rejected: Rejection[];
  /** The room's rev after applying (unchanged when nothing was accepted). */
  roomRev: number;
}

export interface MergeContext {
  /** Mints ids. Injected so the DO can use crypto.randomUUID and tests can be
   * deterministic — an id generator is exactly the kind of ambient dependency
   * that makes a merge untestable. The creating intent's tempId is passed
   * through so a caller that needs DETERMINISTIC ids (the client projection:
   * recomputing must yield byte-identical pseudo-ids) can derive from it; the
   * server ignores it. */
  newId: (kind: "track" | "clip", tempId?: string) => string;
  /** Whether an audio key actually exists in storage. The room refuses to
   * reference audio nobody can fetch, which is the upstream half of the
   * 2026-08-13 wipe: a clip whose bytes aren't there makes every receiver fail
   * the create forever. Return true when the check is unavailable. */
  audioExists?: (key: string) => boolean;
}

/** What one intent did against one room: exactly one of these three. A silent
 * outcome is an idempotent no-op (deleting something already gone) — satisfied,
 * not failed, and it must not abort a gesture. */
type MergeOneResult =
  | { kind: "op"; op: AcceptedOp }
  | { kind: "rejection"; reason: Rejection["reason"]; current: unknown }
  | { kind: "silent" };

/**
 * The audio keys the merge refused as `audio-not-found`, deduplicated, in
 * rejection order. The reject carries the key as `current.audioKey`
 * (below); nothing else about the rejection names it. The JamRoom logs
 * them as its batch line's `missing` (#195); the extension's FakeRoom records
 * the same.
 */
export function missingKeysIn(rejected: Array<{ reason: string; current: unknown }>): string[] {
  const out: string[] = [];
  for (const r of rejected) {
    if (r.reason !== "audio-not-found") continue;
    const key = (r.current as { audioKey?: unknown } | null)?.audioKey;
    if (typeof key === "string" && !out.includes(key)) out.push(key);
  }
  return out.slice(0, 32);
}

/**
 * Merge intents into `room`, mutating it. Ops are applied as they're accepted,
 * so intents within one envelope see each other's effects — a client can create
 * a track and its clips in a single round-trip.
 *
 * GESTURES (v4 step 4): intents sharing a `gestureId` are one user action —
 * today, a move's delete+create pair — and are merged ALL-OR-NOTHING: the
 * members run against a trial clone of the room (with a copied tempId map, so
 * a rejected group's minted ids die with it), and any rejection rejects every
 * member — the failing one with its real reason, the rest `gesture-aborted`.
 * Everything else keeps the per-intent path: whole-envelope atomicity would be
 * WRONG (one poll cycle bundles unrelated edits, and Live's own truncation of
 * an underlying clip must be able to land while the drag that caused it is
 * refused — per-intent merge converges on that; all-or-nothing can reject it
 * forever). Rev semantics unchanged: every accepted op in the envelope shares
 * `roomRev + 1`, bumped once iff anything was accepted — the replica's
 * contiguity check depends on exactly that.
 */
export function mergeIntents(
  room: CanonicalRoom,
  intents: Intent[],
  ctx: MergeContext,
): MergeResult {
  const accepted: AcceptedOp[] = [];
  const rejected: Rejection[] = [];
  // Creates within this envelope: tempId → real id, so a clip can name a track
  // the same envelope just created.
  const minted = new Map<string, string>();
  const nextRoomRev = room.roomRev + 1;
  const startRevs = captureStartRevs(room);

  const reject = (intent: Intent, reason: Rejection["reason"], current: unknown): void => {
    rejected.push({ intent, reason, current });
  };

  // Units, in order of first appearance: a gesture's members merge as one unit
  // at the position of its first member (the compiler does not emit pairs
  // adjacently — deletes come before creates within a lane).
  const groups = new Map<string, Intent[]>();
  for (const intent of intents) {
    if (intent.gestureId !== undefined) {
      const members = groups.get(intent.gestureId) ?? [];
      members.push(intent);
      groups.set(intent.gestureId, members);
    }
  }
  const processedGestures = new Set<string>();

  for (const intent of intents) {
    const gestureId = intent.gestureId;
    const unit =
      gestureId !== undefined && (groups.get(gestureId)?.length ?? 0) > 1
        ? groups.get(gestureId)!
        : [intent];

    if (gestureId !== undefined && unit.length > 1) {
      if (processedGestures.has(gestureId)) continue;
      processedGestures.add(gestureId);

      // Trial: the members run against a CLONE, with a copied tempId map. Only
      // a clean unit commits — its ops (real ids already embedded) replay onto
      // the real room, and its mints join the envelope's map.
      const trial = cloneRoom(room);
      const trialMinted = new Map(minted);
      const groupOps: AcceptedOp[] = [];
      let failure: { intent: Intent; reason: Rejection["reason"]; current: unknown } | null = null;
      for (const member of unit) {
        const r = mergeOne(trial, member, ctx, trialMinted, nextRoomRev, startRevs);
        if (r.kind === "rejection") {
          failure = { intent: member, reason: r.reason, current: r.current };
          break;
        }
        if (r.kind === "op") {
          applyAcceptedOps(trial, [r.op]);
          groupOps.push(r.op);
        }
      }
      if (failure) {
        for (const member of unit) {
          if (member === failure.intent) reject(member, failure.reason, failure.current);
          else reject(member, "gesture-aborted", null);
        }
      } else {
        for (const op of groupOps) {
          applyAcceptedOps(room, [op]);
          accepted.push(op);
        }
        for (const [tempId, id] of trialMinted) {
          if (!minted.has(tempId)) minted.set(tempId, id);
        }
      }
      continue;
    }

    const r = mergeOne(room, intent, ctx, minted, nextRoomRev, startRevs);
    if (r.kind === "op") {
      applyAcceptedOps(room, [r.op]);
      accepted.push(r.op);
    } else if (r.kind === "rejection") {
      reject(intent, r.reason, r.current);
    }
  }

  if (accepted.length > 0) room.roomRev = nextRoomRev;
  return { accepted, rejected, roomRev: room.roomRev };
}

/**
 * Every rev as it stood when the envelope arrived. An intent citing the rev
 * its entity had at envelope START is fresh even after an earlier intent in
 * the same envelope bumped it: one client compiled the whole envelope against
 * one replica, and the bump was its own. Without this a fader move and a send
 * ride on one lane in one poll tick rejected the second as `stale` and sent
 * the client into a full re-adopt (2026-09-08 review). A rev older than the
 * start (the room moved before the envelope arrived) is still stale.
 */
interface StartRevs {
  tracks: Map<string, number>;
  /** Each lane's device chain rev (slice 38). */
  devices: Map<string, number>;
  /** Each non-lane chain's rev, by owner (slice 39). */
  chains: Map<string, number>;
  clips: Map<string, number>;
  tempo: number;
  scenes: number;
  cues: number;
  master: number;
  returns: number;
  scale: number;
}

function captureStartRevs(room: CanonicalRoom): StartRevs {
  return {
    tracks: new Map(room.tracks.map((t) => [t.id, t.rev])),
    devices: new Map(room.tracks.map((t) => [t.id, t.devicesRev ?? 0])),
    chains: new Map(Object.entries(room.chains ?? {}).map(([owner, c]) => [owner, c.rev])),
    clips: new Map(room.clips.map((c) => [c.id, c.rev])),
    tempo: room.tempoRev,
    scenes: room.scenesRev ?? 0,
    cues: room.cuePointsRev ?? 0,
    master: room.masterRev ?? 0,
    returns: room.returnsRev ?? 0,
    scale: room.scaleRev ?? 0,
  };
}

/** A device chain on the wire, validated and cleaned (values clamped into
 * range, the fold's copy), or null when malformed or past the bounds. */
function validDevices(devices: unknown): DeviceState[] | null {
  const validParam = (p: unknown): boolean =>
    typeof p === "object" &&
    p !== null &&
    typeof (p as { name: unknown }).name === "string" &&
    (p as { name: string }).name.length <= MAX_DEVICE_NAME_LENGTH &&
    [(p as { value: unknown }).value, (p as { min: unknown }).min, (p as { max: unknown }).max].every(
      (v) => typeof v === "number" && Number.isFinite(v),
    ) &&
    ((p as { quantized?: unknown }).quantized === undefined || typeof (p as { quantized: unknown }).quantized === "boolean") &&
    ((p as { items?: unknown }).items === undefined ||
      (Array.isArray((p as { items: unknown[] }).items) &&
        (p as { items: unknown[] }).items.length <= MAX_DEVICE_PARAM_ITEMS &&
        (p as { items: unknown[] }).items.every((s) => typeof s === "string" && s.length <= MAX_DEVICE_NAME_LENGTH)));
  if (!Array.isArray(devices) || devices.length > MAX_DEVICES) return null;
  const list = devices as unknown[];
  let total = 0;
  for (const d of list) {
    if (typeof d !== "object" || d === null) return null;
    const { name, params } = d as { name?: unknown; params?: unknown };
    if (typeof name !== "string" || name.length > MAX_DEVICE_NAME_LENGTH || !Array.isArray(params) || params.length > MAX_DEVICE_PARAMS) return null;
    if (!params.every(validParam)) return null;
    total += params.length;
  }
  if (total > MAX_DEVICE_PARAMS_PER_LANE) return null;
  const clean = cloneDevices(list as DeviceState[]);
  for (const d of clean) for (const p of d.params) p.value = clampParam(p.value, p);
  return clean;
}

/** Validate ONE intent against `room` (not yet applied — the caller decides
 * whether this op commits, which is what makes gesture trials possible). */
function mergeOne(
  room: CanonicalRoom,
  intent: Intent,
  ctx: MergeContext,
  minted: Map<string, string>,
  nextRoomRev: number,
  startRevs: StartRevs,
): MergeOneResult {
  const take = (op: AcceptedOp): MergeOneResult => ({ kind: "op", op });
  /** Fresh against the entity's current rev, or its rev at envelope start. */
  const fresh = (current: number, start: number | undefined, baseRev: number): boolean =>
    baseRev === current || (start !== undefined && baseRev === start);
  const reject = (reason: Rejection["reason"], current: unknown): MergeOneResult => ({
    kind: "rejection",
    reason,
    current,
  });

  switch (intent.kind) {
    case "createTrack": {
      // Creates never conflict: two peers adding a lane both get one. Ord is
      // display order, and a collision is cosmetic, never identity. The one
      // exception is a name carrying a ⟨sb:…⟩ token the room already holds:
      // that IS an identity, and a second lane under it is a leftover from
      // another Set (a wipe that could not delete it) re-proposed as new —
      // 2026-10-22, finding 3. The proposer gets the existing lane to bind to.
      const token = typeof intent.name === "string" ? parseTrackName(intent.name).token : null;
      if (token !== null) {
        const held = room.tracks.find((t) => parseTrackName(t.name).token === token);
        if (held) return reject("duplicate", { ...held });
      }
      const id = ctx.newId("track", intent.tempId);
      minted.set(intent.tempId, id);
      return take({
        kind: "trackCreated",
        id,
        ord: intent.ord,
        name: intent.name,
        rev: nextRoomRev,
        tempId: intent.tempId,
        // A lane's kind is fixed at birth; absent means audio (old senders).
        ...(intent.trackKind === "midi" ? { trackKind: "midi" as const } : {}),
        // An instrument rides only on a MIDI lane, and only a known one; any
        // other is dropped rather than rejecting the create.
        ...(intent.trackKind === "midi" && isInstrumentId(intent.instrument) ? { instrument: intent.instrument } : {}),
      });
    }

    case "setTrackField": {
      const t = room.tracks.find((x) => x.id === intent.id);
      if (!t) return reject("unknown-track", null);
      if (!fresh(t.rev, startRevs.tracks.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      // The numeric track fields. The fold clamps into range; a non-finite
      // payload is rejected here so it never revs the track at all.
      if (
        (intent.field === "volume" || intent.field === "pan") &&
        (typeof intent.value !== "number" || !Number.isFinite(intent.value))
      ) {
        return reject("invalid", { ...t });
      }
      // A tokenless rename of a stamped lane keeps its ⟨sb:…⟩ token. Live never
      // writes a tokenless name over a stamped track (apply-ops nameToWrite), so
      // dropping it here only split the room from the Set: the rename never
      // reached Live and the lane lost its identity. Covers clients that send
      // the display half (browser tabs from before the header stripped it).
      const value =
        intent.field === "name" && typeof intent.value === "string"
          ? renameKeepingToken(t.name, intent.value)
          : intent.value;
      return take({
        kind: "trackFieldSet",
        id: t.id,
        field: intent.field,
        value,
        rev: nextRoomRev,
      });
    }

    case "setTrackOrder": {
      const t = room.tracks.find((x) => x.id === intent.id);
      if (!t) return reject("unknown-track", null);
      if (!fresh(t.rev, startRevs.tracks.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      if (typeof intent.ord !== "number" || !Number.isFinite(intent.ord)) return reject("invalid", { ...t });
      return take({ kind: "trackOrderSet", id: t.id, ord: intent.ord, rev: nextRoomRev });
    }

    case "deleteTrack": {
      const t = room.tracks.find((x) => x.id === intent.id);
      // Already gone: the client's goal is satisfied, so this is not a
      // failure. Idempotence matters — retries are normal.
      if (!t) return { kind: "silent" };
      if (!fresh(t.rev, startRevs.tracks.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      return take({ kind: "trackDeleted", id: t.id, rev: nextRoomRev });
    }

    case "upsertClip": {
      const trackId = minted.get(intent.trackId) ?? intent.trackId;
      const track = room.tracks.find((t) => t.id === trackId);
      if (!track) return reject("unknown-track", null);
      const laneKind = track.trackKind ?? "audio";
      const clipKind = intent.clip.clipKind ?? "audio";
      // A clip's kind is its lane's kind — Live cannot hold a MIDI clip on an
      // audio track or the reverse, so the room refuses to either.
      if (clipKind !== laneKind) return reject("invalid", { trackKind: laneKind });
      // A Session View slot is a non-negative integer; anything else can never
      // be represented in Live (there is no half a scene).
      if (
        intent.clip.slot !== undefined &&
        (typeof intent.clip.slot !== "number" || !Number.isInteger(intent.clip.slot) || intent.clip.slot < 0)
      ) {
        return reject("invalid", { slot: intent.clip.slot });
      }
      if (intent.clip.sourceTempo !== undefined && !validSourceTempo(intent.clip.sourceTempo)) {
        return reject("invalid", { sourceTempo: intent.clip.sourceTempo });
      }
      if (intent.clip.warpMarkers !== undefined && !validWarpMarkers(intent.clip.warpMarkers)) {
        return reject("invalid", { warpMarkers: null });
      }
      if (
        intent.clip.slotLooping !== undefined &&
        (clipKind !== "midi" || intent.clip.slot === undefined || typeof intent.clip.slotLooping !== "boolean")
      ) {
        return reject("invalid", { slotLooping: intent.clip.slotLooping });
      }
      if (intent.clip.transpose !== undefined && (clipKind === "midi" || !validPitch(intent.clip.transpose, MAX_TRANSPOSE, true))) {
        return reject("invalid", { transpose: intent.clip.transpose });
      }
      if (intent.clip.detune !== undefined && (clipKind === "midi" || !validPitch(intent.clip.detune, MAX_DETUNE, false))) {
        return reject("invalid", { detune: intent.clip.detune });
      }
      if (intent.clip.warpSettings !== undefined && (clipKind === "midi" || warpSettingsOf(intent.clip.warpSettings) === null)) {
        return reject("invalid", { warpSettings: null });
      }
      if (intent.clip.libraryClip !== undefined && (clipKind === "midi" || !validLibraryRef(intent.clip.libraryClip))) {
        return reject("invalid", { libraryClip: null });
      }
      if (intent.clip.render !== undefined && (clipKind === "midi" || !validRender(intent.clip.render))) {
        return reject("invalid", { render: null });
      }
      // A render the room already holds is not re-checked: a move/edit
      // re-asserts the clip's current content, and the DO only pre-HEADs keys
      // the envelope names (createClip/editClip), so a moveClip of a rendered
      // clip would otherwise be refused as audio-not-found.
      const heldRenderKey = intent.id === null ? undefined : room.clips.find((c) => c.id === intent.id)?.render?.key;
      if (
        intent.clip.render?.key &&
        intent.clip.render.key !== heldRenderKey &&
        ctx.audioExists &&
        !ctx.audioExists(intent.clip.render.key)
      ) {
        return reject("audio-not-found", { audioKey: intent.clip.render.key });
      }
      // Only the file's NAME enters the room (2026-10-22, finding 12): an
      // older client's absolute path is cut here, so no participant's user
      // folder is ever broadcast or kept.
      const incoming: ClipData = {
        ...intent.clip,
        filePath: typeof intent.clip.filePath === "string" ? wireFileName(intent.clip.filePath) : "",
      };
      if (intent.id === null) {
        if (clipKind === "midi") {
          // Create. The content is the notes: a MIDI clip with none is not a
          // clip (the audio rule, transposed). The bounce is optional.
          const notes = intent.clip.notes;
          if (!Array.isArray(notes) || notes.length > MAX_NOTES_PER_CLIP) {
            return reject("missing-notes", null);
          }
        } else {
          // Create. Audio is mandatory: a clip the room cannot serve bytes for
          // is a clip every receiver will fail to build, forever.
          if (!intent.audioKey) return reject("missing-audio", null);
        }
        if (intent.audioKey && ctx.audioExists && !ctx.audioExists(intent.audioKey)) {
          return reject("audio-not-found", { audioKey: intent.audioKey });
        }
        if (overlaps(room, trackId, intent.clip, null)) {
          return reject("overlap", overlapping(room, trackId, intent.clip, null));
        }
        const id = ctx.newId("clip", intent.tempId);
        minted.set(intent.tempId, id);
        return take({
          kind: "clipUpserted",
          id,
          trackId,
          clip: withSourceTempo(incoming, null, true, room),
          audioKey: intent.audioKey,
          rev: nextRoomRev,
          tempId: intent.tempId,
        });
      }
      const existing = room.clips.find((c) => c.id === intent.id);
      if (!existing) return reject("unknown-clip", null);
      if (intent.baseRev === null || !fresh(existing.rev, startRevs.clips.get(existing.id), intent.baseRev)) {
        return reject("stale", { ...existing });
      }
      if ((existing.clipKind ?? "audio") !== clipKind) {
        return reject("invalid", { clipKind: existing.clipKind ?? "audio" });
      }
      // An update may carry no new audio (a rename/colour change) — keep what
      // the room already has rather than nulling it. One exception: a MIDI
      // clip's audio is its Live bounce, rendered from the notes it had, and
      // a note edit makes that bounce a lie — Live will never re-render it
      // for the room — so the edit drops it and the clip sounds through the
      // synth from then on (shared/playback/geometry.ts `clipSounding`).
      const notesEdited =
        clipKind === "midi" && intent.clip.notes !== undefined && intent.clip.notesDigest !== existing.notesDigest;
      const audioKey = intent.audioKey ?? (notesEdited ? null : existing.audioKey);
      if (intent.audioKey && ctx.audioExists && !ctx.audioExists(intent.audioKey)) {
        return reject("audio-not-found", { audioKey: intent.audioKey });
      }
      if (overlaps(room, trackId, intent.clip, existing.id)) {
        return reject("overlap", overlapping(room, trackId, intent.clip, existing.id));
      }
      const newAudio = intent.audioKey !== null && intent.audioKey !== existing.audioKey;
      let clip: ClipData = withSourceTempo(incoming, existing, newAudio, room);
      // An update without these keeps the room's (the reducer's rule). Say so
      // in the op itself, so a host that reads only the op — the extension,
      // deciding whether a clip's render is of this clip — sees the clip the
      // room will hold, not a partial one.
      if (clipKind !== "midi") {
        if (clip.transpose === undefined && existing.transpose !== undefined && !(newAudio && existing.render)) clip = { ...clip, transpose: existing.transpose };
        if (clip.detune === undefined && existing.detune !== undefined && !(newAudio && existing.render)) clip = { ...clip, detune: existing.detune };
        if (clip.warpSettings === undefined && existing.warpSettings !== undefined && !(newAudio && existing.render)) clip = { ...clip, warpSettings: existing.warpSettings };
        if (newAudio && existing.render) {
          // New audio under a clip that had a render is a Live-side edit of the
          // flattened clip (or a bounce): a new object, whose pitch and knobs
          // are not the old one's. Left to "keep the room's" they would be
          // applied a second time on top of audio that already has them.
          if (clip.transpose === undefined) clip = { ...clip, transpose: 0 };
          if (clip.detune === undefined) clip = { ...clip, detune: 0 };
          if (clip.warpSettings === undefined) clip = { ...clip, warpSettings: {} };
        }
        if (!newAudio) {
          if (clip.warpMarkers === undefined && existing.warpMarkers !== undefined) clip = { ...clip, warpMarkers: existing.warpMarkers };
          if (clip.render === undefined && existing.render !== undefined) clip = { ...clip, render: existing.render };
        }
      }
      if (clipKind === "midi") {
        if (intent.clip.notes === undefined) {
          // No notes on an update ⇒ keep the room's (the null-audioKey idiom).
          // A changed digest WITHOUT the notes behind it is incoherent: the
          // sender claims new content it did not send.
          if (
            intent.clip.notesDigest !== undefined &&
            intent.clip.notesDigest !== existing.notesDigest
          ) {
            return reject("invalid", { notesDigest: existing.notesDigest ?? null });
          }
          clip = { ...incoming };
          if (existing.notes !== undefined) clip.notes = existing.notes;
          if (existing.notesDigest !== undefined) clip.notesDigest = existing.notesDigest;
        } else if (intent.clip.notes.length > MAX_NOTES_PER_CLIP) {
          return reject("missing-notes", null);
        }
      }
      return take({
        kind: "clipUpserted",
        id: existing.id,
        trackId,
        clip,
        audioKey,
        rev: nextRoomRev,
      });
    }

    case "deleteClip": {
      const c = room.clips.find((x) => x.id === intent.id);
      if (!c) return { kind: "silent" }; // already gone — idempotent, not a failure
      if (!fresh(c.rev, startRevs.clips.get(c.id), intent.baseRev)) return reject("stale", { ...c });
      return take({ kind: "clipDeleted", id: c.id, trackId: c.trackId, rev: nextRoomRev });
    }

    case "setTempo": {
      if (!fresh(room.tempoRev, startRevs.tempo, intent.baseRev)) {
        return reject("stale", { tempo: room.tempo, tempoRev: room.tempoRev });
      }
      if (typeof intent.value !== "number" || !Number.isFinite(intent.value)) {
        return reject("invalid", { tempo: room.tempo });
      }
      return take({ kind: "tempoSet", value: intent.value, rev: nextRoomRev });
    }

    case "setSceneNames": {
      const current = { sceneNames: room.sceneNames ?? [], scenesRev: room.scenesRev ?? 0 };
      if (!fresh(current.scenesRev, startRevs.scenes, intent.baseRev)) return reject("stale", current);
      const names = intent.names;
      if (
        !Array.isArray(names) ||
        names.length > MAX_SCENES ||
        names.some((n) => n !== null && (typeof n !== "string" || n.length > MAX_SCENE_NAME_LENGTH))
      ) {
        return reject("invalid", current);
      }
      return take({ kind: "sceneNamesSet", names: [...names], rev: nextRoomRev });
    }

    case "setCuePoints": {
      const current = { cuePoints: room.cuePoints ?? [], cuePointsRev: room.cuePointsRev ?? 0 };
      if (!fresh(current.cuePointsRev, startRevs.cues, intent.baseRev)) return reject("stale", current);
      const points = intent.points;
      if (
        !Array.isArray(points) ||
        points.length > MAX_CUE_POINTS ||
        points.some(
          (p) =>
            typeof p !== "object" ||
            p === null ||
            typeof p.name !== "string" ||
            p.name.length > MAX_CUE_NAME_LENGTH ||
            typeof p.time !== "number" ||
            !Number.isFinite(p.time) ||
            p.time < 0,
        )
      ) {
        return reject("invalid", current);
      }
      // Sorted by time so every reader sees one order; the fold copies.
      const sorted = points.map((p) => ({ name: p.name, time: p.time })).sort((a, b) => a.time - b.time);
      return take({ kind: "cuePointsSet", points: sorted, rev: nextRoomRev });
    }

    case "setTrackSends": {
      const t = room.tracks.find((x) => x.id === intent.id);
      if (!t) return reject("unknown-track", null);
      if (!fresh(t.rev, startRevs.tracks.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      const sends = intent.sends;
      if (!Array.isArray(sends) || sends.length > MAX_RETURNS || sends.some((v) => typeof v !== "number" || !Number.isFinite(v))) {
        return reject("invalid", { ...t });
      }
      return take({ kind: "trackSendsSet", id: t.id, sends: sends.map(clampVolume), rev: nextRoomRev });
    }

    case "setTrackInstrument": {
      const t = room.tracks.find((x) => x.id === intent.id);
      if (!t) return reject("unknown-track", null);
      if (!fresh(t.rev, startRevs.tracks.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      if (t.trackKind !== "midi" || !isInstrumentId(intent.instrument)) return reject("invalid", { ...t });
      return take({ kind: "trackInstrumentSet", id: t.id, instrument: intent.instrument, rev: nextRoomRev });
    }

    case "setTrackDevices": {
      const t = room.tracks.find((x) => x.id === intent.id);
      if (!t) return reject("unknown-track", null);
      if (!fresh(t.devicesRev ?? 0, startRevs.devices.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      const clean = validDevices(intent.devices);
      if (!clean) return reject("invalid", { ...t });
      // A reconcile (the extension's poll saying what its Set holds) may
      // DESCRIBE a chain the room has never heard of, or FILL IN a known
      // chain's placeholders (a browser's inserts, by name); it may not
      // reshape a known chain — a wipe, a device dropped, or a joiner's own
      // device grown onto an empty master chain (2026-10-22, finding 1).
      // The musician's own edit of a chain their Set matched travels as
      // `origin: "user"`. And "no devices" against a chain nobody has
      // described is nothing to say, whoever sends it — a client from
      // before `origin` existed included: the room stays unknown, with no
      // rev a Set that does hold devices would then have to cite.
      if (t.devices === undefined && clean.length === 0) return { kind: "silent" };
      if (intent.origin === "reconcile" && t.devices !== undefined && !chainMatches(t.devices, clean)) return reject("shrink", { ...t });
      return take({ kind: "trackDevicesSet", id: t.id, devices: clean, rev: nextRoomRev });
    }

    case "setDeviceParam": {
      const t = room.tracks.find((x) => x.id === intent.id);
      if (!t) return reject("unknown-track", null);
      if (!fresh(t.devicesRev ?? 0, startRevs.devices.get(t.id), intent.baseRev)) return reject("stale", { ...t });
      const p = t.devices?.[intent.device]?.params[intent.param];
      if (
        !Number.isInteger(intent.device) ||
        !Number.isInteger(intent.param) ||
        !p ||
        typeof intent.value !== "number" ||
        !Number.isFinite(intent.value)
      ) {
        return reject("invalid", { ...t });
      }
      return take({
        kind: "deviceParamSet",
        id: t.id,
        device: intent.device,
        param: intent.param,
        value: clampParam(intent.value, p),
        deviceName: t.devices![intent.device]!.name,
        paramName: p.name,
        rev: nextRoomRev,
      });
    }

    case "setChain": {
      if (typeof intent.owner !== "string" || !isChainOwner(intent.owner)) return reject("invalid", null);
      const current = room.chains?.[intent.owner] ?? { devices: [], rev: 0 };
      if (!fresh(current.rev, startRevs.chains.get(intent.owner), intent.baseRev)) return reject("stale", { ...current });
      const clean = validDevices(intent.devices);
      if (!clean) return reject("invalid", { ...current });
      // Same rule as a lane's chain (above): a reconcile describes or fills
      // in, never reshapes, and says nothing empty about an unknown chain.
      if (current.rev === 0 && clean.length === 0) return { kind: "silent" };
      if (intent.origin === "reconcile" && current.rev > 0 && !chainMatches(current.devices, clean)) return reject("shrink", { ...current });
      return take({ kind: "chainSet", owner: intent.owner, devices: clean, rev: nextRoomRev });
    }

    case "setChainParam": {
      if (typeof intent.owner !== "string" || !isChainOwner(intent.owner)) return reject("invalid", null);
      const current = room.chains?.[intent.owner];
      if (!fresh(current?.rev ?? 0, startRevs.chains.get(intent.owner), intent.baseRev)) return reject("stale", current ? { ...current } : null);
      const p = current?.devices[intent.device]?.params[intent.param];
      if (!Number.isInteger(intent.device) || !Number.isInteger(intent.param) || !p || typeof intent.value !== "number" || !Number.isFinite(intent.value)) {
        return reject("invalid", current ? { ...current } : null);
      }
      return take({
        kind: "chainParamSet",
        owner: intent.owner,
        device: intent.device,
        param: intent.param,
        value: clampParam(intent.value, p),
        deviceName: current!.devices[intent.device]!.name,
        paramName: p.name,
        rev: nextRoomRev,
      });
    }

    case "setReturns": {
      const current = { returns: room.returns ?? [], returnsRev: room.returnsRev ?? 0 };
      if (!fresh(current.returnsRev, startRevs.returns, intent.baseRev)) return reject("stale", current);
      const returns = intent.returns;
      if (
        !Array.isArray(returns) ||
        returns.length > MAX_RETURNS ||
        returns.some(
          (r) =>
            typeof r !== "object" ||
            r === null ||
            typeof r.name !== "string" ||
            r.name.length > MAX_SCENE_NAME_LENGTH ||
            typeof r.mute !== "boolean" ||
            typeof r.solo !== "boolean" ||
            typeof r.volume !== "number" ||
            !Number.isFinite(r.volume) ||
            typeof r.pan !== "number" ||
            !Number.isFinite(r.pan),
        )
      ) {
        return reject("invalid", current);
      }
      return take({
        kind: "returnsSet",
        returns: returns.map((r) => ({ name: r.name, mute: r.mute, solo: r.solo, volume: clampVolume(r.volume), pan: clampPan(r.pan) })),
        rev: nextRoomRev,
      });
    }

    case "setMaster": {
      const current = { master: room.master ?? { volume: UNITY_VOLUME, pan: CENTER_PAN }, masterRev: room.masterRev ?? 0 };
      if (!fresh(current.masterRev, startRevs.master, intent.baseRev)) return reject("stale", current);
      if (typeof intent.volume !== "number" || !Number.isFinite(intent.volume) || typeof intent.pan !== "number" || !Number.isFinite(intent.pan)) {
        return reject("invalid", current);
      }
      return take({ kind: "masterSet", volume: clampVolume(intent.volume), pan: clampPan(intent.pan), rev: nextRoomRev });
    }

    case "setScale": {
      const current = { scale: room.scale, scaleRev: room.scaleRev ?? 0 };
      if (!fresh(current.scaleRev, startRevs.scale, intent.baseRev)) return reject("stale", current);
      const scale = validScale(intent.scale);
      if (!scale) return reject("invalid", current);
      return take({ kind: "scaleSet", scale, rev: nextRoomRev });
    }

    default:
      return reject("invalid", null);
  }
}

/**
 * Arrangement clips cannot overlap in Live. Enforcing that HERE keeps canonical
 * state permanently representable, which is the v3 replacement for v2's
 * receiver-side normalization: rather than every receiver independently deciding
 * what to do about geometry Live will rewrite anyway, the room never accepts it.
 *
 * A rejected overlap is not a dead end. When a user drags one clip over another,
 * Live truncates the underlying clip itself, so the proposing client observes and
 * proposes that truncation too — the move lands on the following cycle. Two polls
 * to converge, and canonical state is never briefly impossible.
 *
 * The Session View rule is the same idea in its own geometry: a clip slot holds
 * ONE clip, so a second clip proposed into an occupied slot on the same lane is
 * an "overlap" too (`current` names the occupant). Session clips never collide
 * with timeline clips — they live in a different view of the same track.
 */
/**
 * Warp markers a browser can map through: a bounded list, finite, beats
 * strictly increasing and file seconds never going backwards (Live's
 * markers are monotonic in both). An empty list is "none", valid.
 */
function validWarpMarkers(v: unknown): v is WarpMarkerData[] {
  if (!Array.isArray(v) || v.length > MAX_WARP_MARKERS) return false;
  let beat = Number.NEGATIVE_INFINITY;
  let sec = Number.NEGATIVE_INFINITY;
  for (const m of v as unknown[]) {
    if (typeof m !== "object" || m === null) return false;
    const { beat: b, sec: s } = m as { beat?: unknown; sec?: unknown };
    if (typeof b !== "number" || !Number.isFinite(b) || typeof s !== "number" || !Number.isFinite(s)) return false;
    if (b <= beat || s < sec || s < 0) return false;
    beat = b;
    sec = s;
  }
  return true;
}

/** A library reference: two short non-empty strings, nothing else trusted. */
function validLibraryRef(v: unknown): boolean {
  if (!v || typeof v !== "object") return false;
  const { id, ownerId } = v as { id?: unknown; ownerId?: unknown };
  const ok = (s: unknown): boolean => typeof s === "string" && s.length > 0 && s.length <= MAX_LIBRARY_REF_ID;
  return ok(id) && ok(ownerId);
}

/** A ClipData.render: a bounded hash, an optional bounded key, a tempo that
 * is a Set's (or 0 while the render is only wanted). */
function validRender(v: unknown): boolean {
  if (!v || typeof v !== "object") return false;
  const { key, hash, tempo } = v as { key?: unknown; hash?: unknown; tempo?: unknown };
  const text = (s: unknown): boolean => typeof s === "string" && s.length > 0 && s.length <= MAX_RENDER_FIELD;
  if (!text(hash)) return false;
  if (key !== undefined && !text(key)) return false;
  return typeof tempo === "number" && Number.isFinite(tempo) && tempo >= 0 && tempo <= 999;
}

/** A clip pitch knob's value: finite, within ±`max`, whole when `integer`. */
function validPitch(v: unknown, max: number, integer: boolean): v is number {
  return typeof v === "number" && Number.isFinite(v) && Math.abs(v) <= max && (!integer || Number.isInteger(v));
}

/** A source tempo a Set could hold: finite and within Live's tempo range. */
function validSourceTempo(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= MIN_SOURCE_TEMPO && v <= MAX_SOURCE_TEMPO;
}
const MIN_SOURCE_TEMPO = 20;
const MAX_SOURCE_TEMPO = 999;

/**
 * The clip as the op will carry it, its `sourceTempo` resolved: an explicit
 * one is kept; otherwise a WARPED audio clip whose audio is NEW (a create,
 * or an update shipping a new object — a Set re-rendering at its tempo)
 * is stamped with the room's tempo when one has been asserted, and an
 * update without new audio keeps what the room holds — or nothing, as
 * before: a clip from before the field is never guessed at from a tempo
 * its object may not have been made at, and an old sender's move or
 * rename must not erase a known one. MIDI clips never carry one.
 */
function withSourceTempo(
  clip: ClipData,
  existing: { sourceTempo?: number } | null,
  newAudio: boolean,
  room: CanonicalRoom,
): ClipData {
  if ((clip.clipKind ?? "audio") === "midi") return clip;
  if (clip.sourceTempo !== undefined) return clip;
  const kept = existing?.sourceTempo;
  if (!newAudio) return kept !== undefined ? { ...clip, sourceTempo: kept } : clip;
  if (clip.isWarped && room.tempoRev > 0 && validSourceTempo(room.tempo)) {
    return { ...clip, sourceTempo: room.tempo };
  }
  return kept !== undefined ? { ...clip, sourceTempo: kept } : clip;
}

function overlaps(
  room: CanonicalRoom,
  trackId: string,
  clip: { startTime: number; duration: number; slot?: number },
  ignoreClipId: string | null,
): boolean {
  return overlapping(room, trackId, clip, ignoreClipId) !== null;
}

function overlapping(
  room: CanonicalRoom,
  trackId: string,
  clip: { startTime: number; duration: number; slot?: number },
  ignoreClipId: string | null,
): { id: string; startTime: number; duration: number; slot?: number } | null {
  if (clip.slot !== undefined) {
    for (const other of clipsOfTrack(room, trackId)) {
      if (other.id === ignoreClipId) continue;
      if (other.slot === clip.slot) {
        return { id: other.id, startTime: other.startTime, duration: other.duration, slot: other.slot };
      }
    }
    return null;
  }
  const start = clip.startTime;
  const end = clip.startTime + clip.duration;
  for (const other of clipsOfTrack(room, trackId)) {
    if (other.id === ignoreClipId) continue;
    if (other.slot !== undefined) continue; // a slot clip has no timeline extent
    const otherEnd = other.startTime + other.duration;
    // Touching end-to-start is fine; genuine intersection is not.
    if (start < otherEnd - ASSERT_EPS && other.startTime < end - ASSERT_EPS) {
      return { id: other.id, startTime: other.startTime, duration: other.duration };
    }
  }
  return null;
}

/**
 * Lanes where a client's per-lane digests disagree with the room's — the input
 * to a targeted repair. Replaces the leader's 30s whole-Set fullState: instead
 * of a peer asserting everything at everyone, a client notices it is off and
 * asks the room about the specific lanes that differ.
 */
export function divergentLanes(
  roomLaneDigests: Record<string, string>,
  clientLaneDigests: Record<string, string>,
): string[] {
  const keys = new Set([...Object.keys(roomLaneDigests), ...Object.keys(clientLaneDigests)]);
  const out: string[] = [];
  for (const k of keys) {
    if (roomLaneDigests[k] !== clientLaneDigests[k]) out.push(k);
  }
  return out.sort();
}

/** Track ids whose lane name-key appears in `laneKeys` (repair targeting). */
export function tracksForLaneKeys(room: CanonicalRoom, laneKeys: string[]): string[] {
  const wanted = new Set(laneKeys);
  const seen = new Map<string, number>();
  const out: string[] = [];
  for (const t of room.tracks) {
    const n = seen.get(t.name) ?? 0;
    seen.set(t.name, n + 1);
    if (wanted.has(`${t.name} ${n}`)) out.push(t.id);
  }
  return out;
}

export { clipsOfTrack };
